import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { uploadToCloudinary, toThumbnail } from '../../../lib/cloudinary';

const inp = {
  width: '100%', border: '1.5px solid #DDD', borderRadius: 8,
  padding: '8px 12px', fontSize: '.86rem', outline: 'none',
  boxSizing: 'border-box', fontFamily: "'DM Sans',sans-serif",
};
const emptyForm    = { name: '', description: '', image_url: '', active: true };
const emptyVariant = { name: '', price: '', isi: '5 pcs' };

export default function MasterProduct() {
  const [products, setProducts]       = useState([]);
  const [form, setForm]               = useState(emptyForm);
  const [editId, setEditId]           = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [origImg, setOrigImg]         = useState('');

  // Varian dalam form tambah/edit produk
  const [formVariants, setFormVariants] = useState([]);

  // Varian per produk di panel expand
  const [expandedId, setExpandedId]   = useState(null);
  const [variantData, setVariantData] = useState({});       // { productId: [...] }
  const [editingVar, setEditingVar]   = useState(null);     // { id, name, price, isi }
  const [addingTo, setAddingTo]       = useState(null);     // productId
  const [newVar, setNewVar]           = useState(emptyVariant);

  useEffect(() => { load(); }, []);

  async function load() {
    const { data } = await supabase.from('products').select('*').order('name');
    setProducts(data || []);
  }

  async function loadVariants(productId) {
    const { data } = await supabase
      .from('product_variants')
      .select('*')
      .eq('product_id', productId)
      .order('sort_order');
    setVariantData(p => ({ ...p, [productId]: data || [] }));
  }

  async function toggleExpand(id) {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    await loadVariants(id);
  }

  // ── Simpan produk (+ varian dari form) ──────────────────────
  async function handleSave() {
    if (!form.name.trim()) return;

    const payload = {
      name: form.name.trim(),
      description: form.description,
      image_url: form.image_url,
      active: form.active,
    };

    let prodId = editId;

    if (editId) {
      await supabase.from('products').update(payload).eq('id', editId);
    } else {
      const { data } = await supabase.from('products').insert(payload).select().single();
      prodId = data?.id;
    }

    // Simpan varian dari form
    if (prodId) {
      const valid = formVariants.filter(v => v.name.trim() && v.price);
      for (let i = 0; i < valid.length; i++) {
        const v = valid[i];
        if (v.id) {
          await supabase.from('product_variants')
            .update({ name: v.name, price: parseInt(v.price), isi: v.isi, sort_order: i })
            .eq('id', v.id);
        } else {
          await supabase.from('product_variants')
            .insert({ product_id: prodId, name: v.name, price: parseInt(v.price), isi: v.isi, sort_order: i });
        }
      }
    }

    reset(); load();
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file, 'sapan/products');
      setForm(f => ({ ...f, image_url: url }));
    } catch { alert('Upload gagal, coba lagi.'); }
    setUploading(false);
  }

  async function handleEdit(p) {
    setForm({ name: p.name, description: p.description || '', image_url: p.image_url || '', active: p.active });
    setOrigImg(p.image_url || '');
    setEditId(p.id);
    const { data } = await supabase.from('product_variants').select('*').eq('product_id', p.id).order('sort_order');
    setFormVariants(data ? data.map(v => ({ id: v.id, name: v.name, price: v.price, isi: v.isi })) : []);
    setShowForm(true);
    window.scrollTo(0, 0);
  }

  function reset() {
    setForm(emptyForm); setEditId(null); setShowForm(false);
    setOrigImg(''); setFormVariants([]);
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus produk ini beserta semua variannya?')) return;
    await supabase.from('products').delete().eq('id', id);
    load();
    if (expandedId === id) setExpandedId(null);
  }

  // ── Varian inline di panel expand ───────────────────────────
  async function handleDeleteVar(varId, productId) {
    if (!window.confirm('Hapus varian ini?')) return;
    await supabase.from('product_variants').delete().eq('id', varId);
    loadVariants(productId);
  }

  async function handleSaveEditVar(productId) {
    if (!editingVar.name.trim() || !editingVar.price) return;
    await supabase.from('product_variants')
      .update({ name: editingVar.name, price: parseInt(editingVar.price), isi: editingVar.isi })
      .eq('id', editingVar.id);
    setEditingVar(null);
    loadVariants(productId);
  }

  async function handleAddVar(productId) {
    if (!newVar.name.trim() || !newVar.price) return;
    const existing = variantData[productId] || [];
    await supabase.from('product_variants').insert({
      product_id: productId,
      name: newVar.name.trim(),
      price: parseInt(newVar.price),
      isi: newVar.isi,
      sort_order: existing.length,
    });
    setAddingTo(null);
    setNewVar(emptyVariant);
    loadVariants(productId);
  }

  function setFV(i, k, v) {
    setFormVariants(arr => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  }

  const imgChanged = editId && form.image_url && form.image_url !== origImg;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px', maxWidth: 720 }}>
      <style>{`
        @media(max-width:520px){.pf2{grid-template-columns:1fr!important}}
        .bsm{padding:4px 10px;border-radius:6px;font-size:.72rem;cursor:pointer;font-family:'DM Sans',sans-serif;white-space:nowrap}
        .vrow:hover{background:#F5F5F5}
        .var-inline{display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;padding:10px 16px}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Produk PO</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>Produk frozen & varian untuk pre-order</div>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }}
          style={{ padding: '9px 16px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' }}>
          + Tambah Produk
        </button>
      </div>

      {/* ── Form produk ── */}
      {showForm && (
        <div className="fade-up" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '20px', marginBottom: 18, boxShadow: 'var(--shadow)' }}>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem', marginBottom: 16 }}>
            {editId ? 'Edit Produk' : 'Produk Baru'}
          </div>

          {/* Info dasar */}
          <div className="pf2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ display: 'block', fontSize: '.72rem', color: 'var(--muted)', marginBottom: 4 }}>Nama Produk *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Cireng Ayam Suwir" style={inp} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
              <input type="checkbox" id="prod-active" checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
              <label htmlFor="prod-active" style={{ fontSize: '.82rem' }}>Aktif (tampil di PO)</label>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '.72rem', color: 'var(--muted)', marginBottom: 4 }}>Deskripsi</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }} />
          </div>

          {/* Foto */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: '.72rem', color: 'var(--muted)', marginBottom: 6 }}>Foto Produk</label>
            {editId && origImg && (
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={toThumbnail(origImg)} alt="" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6, border: '2px solid #DDD' }} />
                {imgChanged && <span style={{ fontSize: '.72rem', color: '#27AE60', background: '#E8F5E9', padding: '2px 8px', borderRadius: 20, border: '1px solid #A5D6A7' }}>✓ Akan diganti</span>}
              </div>
            )}
            {!editId && form.image_url && (
              <img src={toThumbnail(form.image_url)} alt="" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6, border: '2px solid #DDD', marginBottom: 8 }} />
            )}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, background: 'var(--cream)', border: '1.5px solid var(--border)', fontSize: '.78rem', cursor: 'pointer' }}>
              {editId && origImg ? '🔄 Ganti Gambar' : '📷 Pilih Gambar'}
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
            {uploading && <span style={{ fontSize: '.76rem', color: 'var(--muted)', marginLeft: 10 }}>Mengunggah…</span>}
          </div>

          {/* Varian dalam form */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <span style={{ fontSize: '.82rem', fontWeight: 600 }}>Varian</span>
                <span style={{ fontSize: '.72rem', color: 'var(--muted)', marginLeft: 8 }}>Kosongkan jika produk tidak punya varian</span>
              </div>
              <button type="button" onClick={() => setFormVariants(v => [...v, { ...emptyVariant }])}
                style={{ padding: '4px 12px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.74rem', cursor: 'pointer' }}>
                + Tambah Baris
              </button>
            </div>
            {formVariants.length > 0 && (
              <div style={{ border: '1px solid #EEE', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px 28px', background: '#F2F2F2', padding: '6px 12px', fontSize: '.7rem', color: 'var(--muted)', fontWeight: 700 }}>
                  <div>Nama Varian</div><div>Harga/Pack (Rp)</div><div>Isi per Pack</div><div></div>
                </div>
                {formVariants.map((v, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px 28px', gap: 6, padding: '7px 10px', borderTop: '1px solid #EEE', alignItems: 'center' }}>
                    <input value={v.name} onChange={e => setFV(i, 'name', e.target.value)}
                      placeholder="Ayam Pedas" style={{ ...inp, padding: '6px 8px', fontSize: '.82rem' }} />
                    <input type="number" value={v.price} onChange={e => setFV(i, 'price', e.target.value)}
                      placeholder="15000" style={{ ...inp, padding: '6px 8px', fontSize: '.82rem' }} />
                    <input value={v.isi} onChange={e => setFV(i, 'isi', e.target.value)}
                      placeholder="5 pcs" style={{ ...inp, padding: '6px 8px', fontSize: '.82rem' }} />
                    <button type="button" onClick={() => setFormVariants(arr => arr.filter((_, idx) => idx !== i))}
                      style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 6, color: '#C62828', cursor: 'pointer', padding: '4px 5px', fontSize: '.82rem' }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={reset} style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.82rem', cursor: 'pointer', color: 'var(--muted)' }}>Batal</button>
            <button onClick={handleSave} style={{ padding: '8px 16px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', cursor: 'pointer' }}>
              {editId ? 'Simpan Perubahan' : 'Tambah Produk'}
            </button>
          </div>
        </div>
      )}

      {/* ── Daftar produk ── */}
      {products.map(p => {
        const isExp = expandedId === p.id;
        const pvList = variantData[p.id] || [];
        return (
          <div key={p.id} style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: 10, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>

            {/* Baris utama produk */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '13px 16px' }}>
              {p.image_url
                ? <img src={toThumbnail(p.image_url)} alt="" style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 50, height: 50, borderRadius: 8, background: 'linear-gradient(135deg,#5C3A1E,#1C1108)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🍟</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '.96rem' }}>{p.name}</div>
                <div style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 2 }}>
                  {!p.active && <span style={{ color: '#C62828', marginRight: 6 }}>Nonaktif</span>}
                  <span style={{ cursor: 'pointer', color: '#0D47A1', textDecoration: 'underline dotted' }} onClick={() => toggleExpand(p.id)}>
                    {isExp ? '▲ Tutup' : '▼ Varian'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                <button className="bsm" onClick={() => handleEdit(p)} style={{ background: 'var(--cream)', border: '1px solid var(--border)' }}>Edit</button>
                <button className="bsm" onClick={() => handleDelete(p.id)} style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828' }}>Hapus</button>
              </div>
            </div>

            {/* Panel varian */}
            {isExp && (
              <div style={{ borderTop: '1px solid var(--border)', background: '#FAFAFA' }}>

                {pvList.length === 0 && addingTo !== p.id && (
                  <div style={{ padding: '12px 16px', fontSize: '.8rem', color: 'var(--muted)' }}>
                    Produk ini tidak punya varian — pelanggan memesan langsung tanpa pilih varian.
                  </div>
                )}

                {/* Header kolom */}
                {pvList.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 80px 120px', padding: '7px 16px', background: '#F2F2F2', fontSize: '.7rem', color: 'var(--muted)', fontWeight: 700 }}>
                    <div>Nama Varian</div><div>Harga/Pack</div><div>Isi</div><div>Aksi</div>
                  </div>
                )}

                {pvList.map(v => {
                  const isEditing = editingVar?.id === v.id;
                  return (
                    <div key={v.id}>
                      <div className="vrow" style={{ display: 'grid', gridTemplateColumns: '1fr 110px 80px 120px', padding: '9px 16px', borderTop: '1px solid #EBEBEB', alignItems: 'center' }}>
                        <div style={{ fontWeight: 500, fontSize: '.84rem' }}>{v.name}</div>
                        <div style={{ fontSize: '.81rem' }}>Rp {(v.price || 0).toLocaleString('id-ID')}</div>
                        <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{v.isi}</div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button className="bsm" onClick={() => setEditingVar(isEditing ? null : { id: v.id, name: v.name, price: v.price, isi: v.isi })}
                            style={{ background: 'var(--cream)', border: '1px solid var(--border)' }}>Edit</button>
                          <button className="bsm" onClick={() => handleDeleteVar(v.id, p.id)}
                            style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828' }}>✕</button>
                        </div>
                      </div>

                      {isEditing && (
                        <div className="var-inline" style={{ background: '#FFF8E1', borderTop: '1px solid #FFE082' }}>
                          {[['Nama', 'name', 'text', 2], ['Harga (Rp)', 'price', 'number', 1], ['Isi', 'isi', 'text', 1]].map(([l, k, t, flex]) => (
                            <div key={k} style={{ flex, minWidth: 80 }}>
                              <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginBottom: 3 }}>{l}</div>
                              <input type={t} value={editingVar[k]} onChange={e => setEditingVar(ev => ({ ...ev, [k]: e.target.value }))}
                                style={{ ...inp, padding: '6px 8px', fontSize: '.82rem' }} />
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: 6, paddingTop: 19 }}>
                            <button className="bsm" onClick={() => handleSaveEditVar(p.id)} style={{ background: '#CD5226', color: '#fff', border: 'none' }}>Simpan</button>
                            <button className="bsm" onClick={() => setEditingVar(null)} style={{ background: 'var(--cream)', border: '1px solid var(--border)' }}>Batal</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Tambah varian inline */}
                {addingTo === p.id ? (
                  <div className="var-inline" style={{ background: '#E8F5E9', borderTop: '1px solid #A5D6A7' }}>
                    {[['Nama Varian', 'name', 'text', 2], ['Harga (Rp)', 'price', 'number', 1], ['Isi', 'isi', 'text', 1]].map(([l, k, t, flex]) => (
                      <div key={k} style={{ flex, minWidth: 80 }}>
                        <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginBottom: 3 }}>{l}</div>
                        <input type={t} placeholder={k === 'name' ? 'Keju' : k === 'price' ? '18000' : '5 pcs'}
                          value={newVar[k]} onChange={e => setNewVar(f => ({ ...f, [k]: e.target.value }))}
                          style={{ ...inp, padding: '6px 8px', fontSize: '.82rem' }} />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, paddingTop: 19 }}>
                      <button className="bsm" onClick={() => handleAddVar(p.id)} style={{ background: '#1B5E20', color: 'white', border: 'none' }}>+ Tambah</button>
                      <button className="bsm" onClick={() => { setAddingTo(null); setNewVar(emptyVariant); }} style={{ background: 'var(--cream)', border: '1px solid var(--border)' }}>Batal</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '8px 16px', borderTop: pvList.length > 0 ? '1px dashed #DDD' : 'none' }}>
                    <button className="bsm" onClick={() => setAddingTo(p.id)} style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', color: '#1B5E20' }}>
                      + Tambah Varian
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {products.length === 0 && !showForm && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: '.88rem' }}>
          Belum ada produk — klik "+ Tambah Produk"
        </div>
      )}
    </div>
  );
}
